import React, { useState, useEffect } from 'react';
import { capitalize } from 'lodash';
import _filter from 'lodash/filter';
import get from 'lodash/get';
import _includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import toPairs from 'lodash/toPairs';
import { categories } from '../../modules/Home/components/SectionThree/utils/categoryFormatter';
import { vehicleTypeNameByNumber } from '../../modules/VehicleRegistration/utils/functions';
import { callAll } from '../../utils/callAll';
import {
  compareKeysToUrl,
  compareTitles,
  compareVehicleNumbers,
} from '../../utils/comparationFilters';
import { formatCurrency } from '../../utils/formatNumber';
import { MOTORCYCLE_NUMBER, NAUTICAL_VEHICLE_NUMBER, PARTS_NUMBER } from '../../utils/productType';
import { Chip } from './Chip';

let url = '';

interface Props {
  items: any;
  onRemove: (itemIndex: any, normalizeId: string, category: number) => void;
  onRemoveChekboxChecked: any;
  searchValue?: string;
  order?: any;
  partCategories: any[];
  partSubCategories: any[];
  partSubSubCategories: any[];
}

export const Chips = ({
  items,
  onRemove,
  onRemoveChekboxChecked,
  searchValue,
  partCategories,
  partSubCategories,
  partSubSubCategories,
}: Props) => {
  const [chips, setChips] = useState(items);

  useEffect(() => {
    setChips(items);
  }, [items]);

  if (!chips || chips.length === 0) {
    return null;
  }

  const handleChipRemove = (itemIndex: any, normalizeId: string) => {
    onRemove(itemIndex, normalizeId, get(chips, 'type.eq', ''));
  };

  const handleClearAll = () => {
    setChips([]);
  };

  const renderChip = (item: any, key: any, isPartCategorie = false) => {
    if (!item) {
      return null;
    }

    if (key === 'motor' && chips.type.eq === MOTORCYCLE_NUMBER) {
      item = `${item} cilindradas`;
    }

    if (
      isPartCategorie &&
      partCategories?.length > 0 &&
      partSubCategories &&
      partSubSubCategories
    ) {
      const isCategorie = partCategories.find(obj => obj.value === item);
      const isSubCategorie = partSubCategories.find(obj => obj.value === item);
      const isSubSubCategorie = partSubSubCategories.find(obj => obj.value === item);

      let label = '';
      if (isCategorie) {
        label = `${capitalize(isCategorie.label)}`;
      }

      if (isSubCategorie) {
        const categorie = partCategories.find(obj => (obj.value = isSubCategorie.parent));
        label = `${capitalize(categorie.label)} > ${capitalize(isSubCategorie.label)}`;
      }

      if (isSubSubCategorie) {
        const subCategorie = partSubCategories.find(
          obj => obj.value === isSubSubCategorie.subParent,
        );
        const categorie = partCategories.find(obj => (obj.value = isSubSubCategorie.parent));
        label = `${capitalize(categorie.label)} > ${capitalize(subCategorie.label)} > ${capitalize(
          isSubSubCategorie.label,
        )}`;
      }

      return (
        <Chip idCheck={item} onRemove={handleChipRemove} index={key} key={`${key}_${item}`}>
          {label}
        </Chip>
      );
    }

    return (
      <Chip idCheck={item} onRemove={handleChipRemove} index={key} key={`${key}_${item}`}>
        {item}
      </Chip>
    );
  };

  const setUrl = (item: string) => {
    if (url) {
      url = url.replace('?', '') + `&${item}`;
      url = url.split('&').sort().join('&');
    } else {
      url = item;
    }
    url = `?${url}`;
  };

  const setUrlObject = (key: any, item: any) => {
    if (!url) {
      url = `?${key}=${item}`;
    } else {
      if (url.indexOf(key) === -1) {
        url = `?${url}&${key}=${item}`;
        url = url.replace('??', '?');
        return;
      }

      let newArray = url.split('&');

      newArray = map(newArray, itemArray => {
        const itemsArray = itemArray.split('=');
        itemsArray[0] = itemsArray[0].replace('?', '');
        if (itemsArray[0] === key) {
          const newItems = `${itemsArray[1]}_${item}`;
          return (itemArray = `${key}=${newItems}`);
        }
        return itemArray;
      });

      url = `${newArray.join('&')}`;
    }

    url = url[0] !== '?' ? `?${url}` : url;
  };

  const removeStateVehiclesFromOptionals = (optionalsToInclude: string[]) => {
    return _filter(optionalsToInclude, optional => !_includes(['0 KM', 'Usado'], optional));
  };

  const chipsToRender = React.useMemo(() => {
    url = '';
    if (searchValue && chips.type.eq === PARTS_NUMBER) {
      renderChip(searchValue, 'pesquisar');
    }
    const component = map(chips, (value: any, key: any) => {
      const keyFormatted = compareKeysToUrl(key);
      const optionalsToInclude = get(value, 'include', []);
      const optionalsNotInclude = get(value, 'not.include', []);

      if (key === 'optionals' || key === 'services') {
        if (optionalsToInclude.length) {
          const newOptionalsToInclude = removeStateVehiclesFromOptionals(optionalsToInclude);

          return map(newOptionalsToInclude, includeItems => {
            if (!isArray(includeItems)) {
              setUrlObject(keyFormatted, includeItems);
              return renderChip(includeItems, key);
            }

            return map(includeItems, includeItem => {
              setUrlObject(keyFormatted, includeItem);
              return renderChip(includeItem, key);
            });
          });
        }

        if (optionalsNotInclude.length) {
          return null;
        }
      }

      if (key === 'stateVehicle') {
        setUrl(`condicao=${value.eq}`);
        return renderChip(value.eq, key);
      }

      if (key === 'city') {
        const { city, state } = chips;

        const textCity = city.eq;
        const textState = state.eq ? `-${state.eq}` : '';

        const text = `${textCity}${textState}`;

        setUrl(`${keyFormatted}=${text}`);

        return renderChip(text, key);
      }

      if (key === 'search') {
        setUrl(`${keyFormatted}=${value}`);
        return renderChip(value, keyFormatted);
      }

      if (key === 'state') {
        return null;
      }

      const newValue: any = toPairs(value)[0];

      if (key === 'nome') {
        setUrl(`${keyFormatted}=${newValue[1]}`);

        return renderChip(newValue[1], key);
      }

      if (newValue[0] === 'between') {
        let text: any = 0;

        if (
          (newValue[1].start || newValue[1].start === 0) &&
          (newValue[1].end || newValue[1].end === 0)
        ) {
          text = `${newValue[1].start} a ${newValue[1].end}`;

          if (key === 'mileage') {
            text =
              chips.type.eq === NAUTICAL_VEHICLE_NUMBER
                ? `${newValue[1].start}h a ${newValue[1].end}h`
                : `${newValue[1].start}km a ${newValue[1].end}km`;
          }

          if (key === 'price') {
            text = `${formatCurrency(newValue[1].start)} a ${formatCurrency(newValue[1].end)}`;
          }

          if (key === 'seatsAmount') {
            text = `${newValue[1].start} a ${newValue[1].end}`;
            setUrl(`${keyFormatted}=${text}`);
            return renderChip(`${text} assentos`, key);
          }
        }

        setUrl(`${keyFormatted}=${text}`);

        return renderChip(text, key);
      }

      if (typeof newValue[1] === 'object') {
        if (key === 'userType') {
          return map(newValue[1], item => {
            setUrlObject(keyFormatted, compareTitles(item));
            return renderChip(compareTitles(item), key);
          });
        }

        if (key === 'ports') {
          return map(newValue[1], item => {
            setUrlObject(keyFormatted, item);
            return renderChip(`${item} portas`, key);
          });
        }

        if (key === 'marches') {
          return map(newValue[1], item => {
            setUrlObject(keyFormatted, item);
            return renderChip(`${item} marchas`, key);
          });
        }

        if (key === 'part_categorie') {
          const [selectedCategorie] = newValue[1];
          setUrlObject(keyFormatted, selectedCategorie);
          return renderChip(selectedCategorie, key, true);
        }

        return map(newValue[1], item => {
          setUrlObject(keyFormatted, item);
          return renderChip(item, key);
        });
      }

      if (key === 'ports') {
        newValue[1] = `${newValue[1]} portas`;
      }

      if (key === 'marches') {
        newValue[1] = `${newValue[1]} marchas`;
      }

      if (key === 'type') {
        newValue[1] = vehicleTypeNameByNumber(newValue[1]);
      }

      if (key === 'userType') {
        setUrl(`${keyFormatted}=${compareTitles(newValue[1])}`);
        return renderChip(compareTitles(newValue[1]), key);
      }

      setUrl(`${keyFormatted}=${newValue[1]}`);
      const valueChip =
        key === 'modelType' && chips.type.eq === PARTS_NUMBER
          ? compareVehicleNumbers(newValue[1])
          : compareTitles(newValue[1]);
      return renderChip(valueChip, key);
    });
    if (location?.search && decodeURI(location?.search).toLowerCase() !== url.toLowerCase()) {
      window.history.pushState(null, '', url);
    }
    return component;
  }, [chips]);

  const clearAllButton = () => {
    if (!isEmpty(chips)) {
      return (
        <li className="flex mb12">
          <button
            className="pointer bg-transparent bn pa0 flex ttu red ml4 fw7 underline f13"
            onClick={callAll(onRemoveChekboxChecked, handleClearAll)}
          >
            Limpar tudo
          </button>
        </li>
      );
    }
  };

  return (
    <ul className="list pa0 flex items-center flex-wrap mt16 mt12-ns mt16-m mt16-l mb0">
      {chipsToRender}
      {clearAllButton()}
    </ul>
  );
};
